import { useState, useEffect, useRef } from 'react';
import { API } from '@/App';

/**
 * Standalone OAuth callback handler — renders OUTSIDE of AuthProvider
 * to avoid the race condition where checkAuth() fires before session exchange.
 * 
 * Flow: Extract session_id → POST to backend → Set cookie → Redirect to dashboard
 */
export default function OAuthCallbackHandler() {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from hash or URL
      const sessionId = extractSessionIdFromUrl();
      
      if (!sessionId) {
        console.error('No session_id found. Hash:', window.location.hash, 'Href:', window.location.href);
        setStatus('error');
        setError('No session ID found');
        // Redirect to login after a brief pause
        setTimeout(() => { window.location.href = '/login'; }, 1500);
        return;
      }

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Session exchange failed:', response.status, errorData);
          setStatus('error');
          setError('Session exchange failed');
          setTimeout(() => { window.location.href = '/login'; }, 1500);
          return;
        }

        // Session cookie is now set by the backend
        // Do a FULL page navigation to /dashboard so AuthProvider boots fresh with the cookie
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        window.location.replace(window.location.origin + '/dashboard');
      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setError('Connection error');
        setTimeout(() => { window.location.href = '/login'; }, 1500);
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#060A12' }}>
      <div className="text-center">
        <div style={{ fontFamily: 'monospace', color: '#0ABFA3', fontSize: '18px', marginBottom: '8px' }}>
          {status === 'error' ? '✕' : <span className="ascii-spinner" style={{ marginRight: '8px' }}></span>}
          {status === 'error' ? 'AUTH FAILED' : 'AUTHENTICATING'}
        </div>
        <p style={{ fontFamily: 'monospace', color: '#3D5A70', fontSize: '14px' }}>
          {status === 'error' ? (error || 'Redirecting to login...') : 'Establishing secure session...'}
        </p>
      </div>
    </div>
  );
}

function extractSessionIdFromUrl() {
  // Check hash (standard Emergent Auth format: #session_id=xxx)
  const hash = window.location.hash || '';
  const hashMatch = hash.match(/session_id=([^&\s]+)/);
  if (hashMatch) return hashMatch[1];

  // Check full href as fallback
  const href = window.location.href || '';
  const hrefMatch = href.match(/[#?&]session_id=([^&#\s]+)/);
  if (hrefMatch) return hrefMatch[1];

  // Check search params
  const params = new URLSearchParams(window.location.search);
  return params.get('session_id') || null;
}
